import React, {forwardRef, useMemo, useImperativeHandle, useRef} from 'react'
import Konva from 'konva'
import {Factory} from 'konva/lib/Factory'
import {Path} from 'react-konva'
import {validateNumber} from '../../helpers/shapes/validateNumber'
import {calcScale} from '../../helpers/shapes/calcScale'

/**
 * @typedef {import('konva/lib/shapes/Path').PathConfig} PathConfig
 * @typedef {import('konva/lib/shapes/Path').Path} Path
 * @typedef {import('../../helpers/shapes/types').Dimensions} Dimensions
 *
 * @typedef {Object} CoreProps
 * @property {number} [rawWidth] - Raw width of the shape.
 * @property {number} [rawHeight] - Raw height of the shape.
 * @property {boolean} [strokeScaleEnabled=false] - Whether to enable stroke scale.
 * @property {Dimensions} [size] - Base size or explicit dimensions when width/height are omitted.
 *
 * @typedef {PathConfig & CoreProps} CustomPathShapeProps
 * @typedef {import('react-konva').KonvaNodeComponent<Path, CustomPathShapeProps>} CustomPath
 */

Factory.addGetterSetter(Konva.Path, 'rawWidth', undefined, validateNumber, function () {
    this.offsetX(this.rawWidth() / 2)
})
Factory.addGetterSetter(Konva.Path, 'rawHeight', undefined, validateNumber, function () {
    this.offsetY(this.rawHeight() / 2)
})

/**
 * Custom path shape component
 * @type {CustomPath}
 */
const CustomPath = forwardRef(({strokeScaleEnabled, ...props}, ref) => {
    const shapeRef = useRef(null)

    useImperativeHandle(ref, () => shapeRef.current, [])

    const scale = useMemo(() => {
        const {rawWidth, rawHeight, size, width, height} = props

        if (!rawWidth || !rawHeight) return {scaleX: 1, scaleY: 1}
        if (!size && (!width || !height)) return {scaleX: 1, scaleY: 1}

        return calcScale(
            {
                width: rawWidth,
                height: rawHeight,
            },
            size ? size : {width, height},
            !!size,
        )
    }, [props?.rawWidth, props?.rawHeight, props?.size, props?.width, props?.height])

    return (
        <Path
            {...props}
            ref={shapeRef}
            scaleX={props.scaleX ?? scale.scaleX}
            scaleY={props.scaleY ?? scale.scaleY}
            strokeScaleEnabled={strokeScaleEnabled ?? false}
        />
    )
})

export default CustomPath
